import React, { useEffect, useState, useCallback } from 'react';
import { useContent } from '../../hooks/useContent';
import { Link } from 'react-router-dom';
import { xPath } from '../../lib/basePath';
import { Button } from '../ui/button';
import { Cookie } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';

// PostHog is not initialised until consent — index.html only defines the stub
// and exposes __phInit(), which is what actually pulls in array.js (~238 KB).
function enableAnalytics() {
  if (typeof window === 'undefined') return;
  try {
    if (typeof window.__phInit === 'function') window.__phInit();
    const ph = window.posthog;
    if (!ph) return;
    ph.opt_in_capturing();
    if (typeof ph.startSessionRecording === 'function') ph.startSessionRecording();
    ph.capture('$pageview');
  } catch (e) {
    /* ignore */
  }
}

// Nothing to switch off when PostHog was never initialised: the stub queues the
// call forever, which is harmless but pointless.
function disableAnalytics() {
  const ph = typeof window !== 'undefined' ? window.posthog : null;
  if (!ph || !window.__phInitDone) return;
  try {
    ph.opt_out_capturing();
  } catch (e) {
    /* ignore */
  }
}

export const CookieConsent = () => {
  const { cookies } = useContent();
  const [open, setOpen] = useState(false);

  // Apply the stored choice on load; show the banner only if no choice yet.
  useEffect(() => {
    let choice = null;
    try {
      choice = localStorage.getItem(CONSENT_KEY);
    } catch (e) {
      /* ignore */
    }
    if (choice === 'accepted') enableAnalytics();
    else if (choice === 'declined') disableAnalytics();
    else setOpen(true);
  }, []);

  // Let the footer "Manage cookies" link reopen the banner (consent withdrawal).
  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener('open-cookie-settings', reopen);
    return () => window.removeEventListener('open-cookie-settings', reopen);
  }, []);

  const choose = useCallback((accepted) => {
    try {
      localStorage.setItem(CONSENT_KEY, accepted ? 'accepted' : 'declined');
    } catch (e) {
      /* ignore */
    }
    if (accepted) enableAnalytics();
    else disableAnalytics();
    setOpen(false);
  }, []);

  if (!cookies || !open) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:max-w-md z-[60] animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-label={cookies.title}
      data-testid="cookie-consent"
    >
      <div
        className="rounded-2xl border border-border/70 bg-card/95 backdrop-blur-xl p-5 sm:p-6 shadow-2xl"
        style={{ boxShadow: '0 0 60px -20px hsl(var(--primary) / 0.25)' }}
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-4 h-4 text-primary" />
          </div>
          <h3 className="text-base font-semibold text-foreground">{cookies.title}</h3>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
          {cookies.message}{' '}
          <Link
            to={xPath(cookies.privacyHref)}
            className="text-primary hover:underline underline-offset-2"
            onClick={() => setOpen(false)}
          >
            {cookies.privacy}
          </Link>
        </p>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 rounded-full h-10 text-sm border-border/60 hover:border-primary/40 cursor-pointer"
            onClick={() => choose(false)}
            data-testid="cookie-decline"
          >
            {cookies.decline}
          </Button>
          <Button
            className="flex-1 rounded-full h-10 text-sm cursor-pointer"
            onClick={() => choose(true)}
            data-testid="cookie-accept"
          >
            {cookies.accept}
          </Button>
        </div>
      </div>
    </div>
  );
};
